import { useState, useRef, useMemo, useEffect } from "react";
import { Client } from "../types/client";
import { useDataCleaningContext } from "../context/data-cleaning-context";
import useCleaningData from "./use-cleaning-data";

interface UseClientSelectionReturn {
  clients: Client[];
  selectAll: boolean;
  displayedClients: Client[];
  unselectedClients: Client[];
  selectedCount: number;
  selectedClientIds: number[];
  isLoading: boolean;
  error: Error | undefined;
  handleClientToggle: (clientId: string) => void;
  handleSelectAll: () => void;
  handleAddClient: (client: Client | null) => void;
  handleClearSelection: () => void;
  refetch: () => void;
  markAsAdvanced: () => void;
  hasAdvanced: boolean;
  completedStepIndex: number | null;
  resetCompletion: () => void;
}

const useClientSelection = (): UseClientSelectionReturn => {
  const { selection, resetSelection } = useDataCleaningContext();
  const { clients: clientsData, isLoading, error, refetch } = useCleaningData();

  const initialClients = useMemo(
    () =>
      clientsData.map((client) => ({
        id: client.id.toString(),
        name: client.name,
        selected: false,
        inView: true,
      })),
    [clientsData],
  );

  const [clients, setClients] = useState<Client[]>(initialClients);
  const [selectAll, setSelectAll] = useState(false);
  const hasAdvancedRef = useRef(false);
  const completedStepIndexRef = useRef<number | null>(null);

  // Update clients state when API data changes, but preserve selections from context
  useEffect(() => {
    const updatedClients = initialClients.map((client) => {
      // Check if this client is selected in context
      const isSelected = selection.selectedClientIds.includes(
        Number.parseInt(client.id, 10),
      );

      return {
        ...client,
        selected: isSelected,
      };
    });

    setClients(updatedClients);

    // Update selectAll state if all displayed clients are selected
    const displayedClients = updatedClients.filter((c) => c.inView);
    const allDisplayedSelected = displayedClients.every((c) => c.selected);
    setSelectAll(allDisplayedSelected && displayedClients.length > 0);
  }, [initialClients, selection.selectedClientIds]);

  const handleClientToggle = (clientId: string) => {
    setClients((prev) => {
      const updated = prev.map((client) =>
        client.id === clientId
          ? { ...client, selected: !client.selected }
          : client,
      );
      const displayedClients = updated.filter((c) => c.inView);
      const allDisplayedSelected = displayedClients.every((c) => c.selected);
      setSelectAll(allDisplayedSelected && displayedClients.length > 0);

      return updated;
    });
  };

  const handleSelectAll = () => {
    setClients((prev) =>
      prev.map((client) =>
        client.inView ? { ...client, selected: !selectAll } : client,
      ),
    );
    setSelectAll(!selectAll);
  };

  const handleAddClient = (client: Client | null) => {
    if (client) {
      setClients((prev) =>
        prev.map((c) => (c.id === client.id ? { ...c, inView: true } : c)),
      );
    }
  };

  const handleClearSelection = () => {
    // Reset to initial state
    setClients(initialClients);
    setSelectAll(false);
    hasAdvancedRef.current = false;
    // Reset context state (clears selectedClientIds, entities, fields)
    resetSelection();
    // Refetch to ensure fresh data
    refetch();
  };

  const markAsAdvanced = () => {
    hasAdvancedRef.current = true;
  };

  const resetCompletion = () => {
    completedStepIndexRef.current = null;
  };

  const displayedClients = clients.filter((client) => client.inView);
  const unselectedClients = clients.filter((client) => !client.inView);
  const selectedCount = clients.filter((c) => c.selected).length;
  const selectedClientIds = clients
    .filter((c) => c.selected)
    .map((c) => Number.parseInt(c.id, 10));

  return {
    clients,
    selectAll,
    displayedClients,
    unselectedClients,
    selectedCount,
    selectedClientIds,
    isLoading,
    error,
    handleClientToggle,
    handleSelectAll,
    handleAddClient,
    handleClearSelection,
    refetch,
    markAsAdvanced,
    hasAdvanced: hasAdvancedRef.current,
    completedStepIndex: completedStepIndexRef.current,
    resetCompletion,
  };
};

export default useClientSelection;
